/**
 * BRRApp - Main Application Router
 * Manages page state and navigation between BRR workflow pages
 *
 * Pages are imported from the pages/ directory and rendered based on current route state.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, Upload, CheckSquare, FileText, Users, CheckCircle, BarChart3, AlertCircle } from 'lucide-react';
import { createBRRApi, createSessionApi } from './services';
import { SessionStatus } from './services/sessionStatus';
import { getApiToken } from './services/apiToken';
import { AdditionalEvidenceItem, Batch, BoxArchiveInfo, BRRLevel, DocumentCounts, Priority, UserRole, ViewType } from './services/types';
import { ErrorBoundary, Footer, ProgressOverlay } from './components';
import { useProgressTracking } from './hooks';
import { ProgressOverlayData } from './services/types';
import { CHEPLAPHARM_LOGO } from './assets/logoData';
import {
  DashboardPage,
  StartBRRPage,
  FormPage,
  ConfirmationPage,
  HomePage,
  SearchPage,
  DocumentValidationPage,
  LoadingPage,
  ConnectionStatusPage
} from './pages';
import type { StartBRROptions } from './pages/StartBRRPage';
import { ValidatedStateProvider } from './contexts/ValidatedStateContext';
import { APP_VERSION } from './version';
import { formatVersionAsReleaseDate, formatBuildNumberTooltip } from './utils/formatVersion';
import { isAtLeast } from './utils/userRole';
import { getCachedIdToken } from './auth/msalToken';
import { readEnv } from './env';

const STAGE_NAME = readEnv('VITE_STAGE_NAME') ?? '';
const BUILD_TOOLTIP = formatBuildNumberTooltip(readEnv('VITE_BUILD_NUMBER'));

// Known page keys for the display-toggle system (static, hoisted to module scope)
const KNOWN_PAGES: ViewType[] = ['home', 'dashboard', 'start-brr', 'form', 'qp-review', 'document-validation', 'search-page', 'connection-status'];

// URL <-> view mapping. The URL is the source of truth for the current view
// (React Router); rendering still uses the #823 keep-mounted display-toggle.
const VIEW_PATHS: Record<ViewType, string> = {
  'home':                '/home',
  'dashboard':           '/dashboard',
  'start-brr':           '/start',
  'document-validation': '/validation',
  'form':                '/form',
  'qp-review':           '/review',
  // 'search' is a vestigial ViewType alias; the real page is 'search-page'.
  // Both map to /search, ordered so PATH_TO_VIEW['/search'] resolves to 'search-page'.
  'search':              '/search',
  'search-page':         '/search',
  'connection-status':   '/status',
};

const PATH_TO_VIEW: Record<string, ViewType> = Object.fromEntries(
  Object.entries(VIEW_PATHS).map(([view, path]) => [path, view as ViewType])
) as Record<string, ViewType>;

const pathToView = (pathname: string): ViewType => PATH_TO_VIEW[pathname] ?? 'home';

interface BRRAppProps {
  // Props passed from main.tsx
  apiBaseUrl?: string;
  userEmail?: string;
  userRole?: UserRole;
}

export const BRRApp: React.FC<BRRAppProps> = ({
  apiBaseUrl = 'https://your-api.azurewebsites.net',
  userEmail = 'user@company.com',
  userRole = 'viewer',
}) => {
  const isAdmin = userRole === 'admin';
  const isSupervisorOrAbove = isAtLeast(userRole, 'supervisor');

  const BRR_SESSION_STORAGE_KEYS = [
    'brr_batch_id', 'brr_session_id', 'brr_inspection_lot_id', 'brr_product_name',
    'brr_batch_type', 'brr_priority', 'brr_brr_level',
    'brr_orchestrator_instance_id', 'brr_blob_prefix',
    'brr_orchestrator_just_completed'
  ];

  function clearBrrSessionStorage() {
    BRR_SESSION_STORAGE_KEYS.forEach(key => {
      try { sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
    });
  }

  // Determine initial page on app load/refresh
  // Always start at home - users can resume work via Recent Activity section
  const getInitialView = (): ViewType => {
    try {
      // If a stale orchestrator is detected, go straight to start-brr
      // so the kill-on-mount effect can clean up in the background.
      if (sessionStorage.getItem('brr_orchestrator_instance_id')) {
        return 'start-brr';
      }
    } catch (e) {
      console.warn('Failed to check sessionStorage:', e);
    }

    // Localhost: support ?page= parameter for direct navigation
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      const params = new URLSearchParams(window.location.search);
      const page = params.get('page') as ViewType | null;
      const validPages: ViewType[] = ['home', 'dashboard', 'start-brr', 'form', 'qp-review', 'document-validation', 'search-page'];
      if (page && validPages.includes(page)) {
        return page;
      }
    }

    if (userRole === 'viewer') return 'dashboard';
    return 'home';
  };

  const getInitialBatchId = (): string | null => {
    try {
      return sessionStorage.getItem('brr_batch_id') || null;
    } catch (e) {
      return null;
    }
  };

  const getInitialSessionId = (): string | null => {
    try {
      return sessionStorage.getItem('brr_session_id') || null;
    } catch (e) {
      return null;
    }
  };

  const getInitialInspectionLotId = (): string | null => {
    try {
      return sessionStorage.getItem('brr_inspection_lot_id') || null;
    } catch (e) {
      return null;
    }
  };

  const getInitialProductName = (): string | null => {
    try {
      return sessionStorage.getItem('brr_product_name') || null;
    } catch (e) {
      return null;
    }
  };

  const getInitialBatchType = (): 'direct' | 'indirect' => {
    try {
      const saved = sessionStorage.getItem('brr_batch_type');
      return (saved === 'indirect' ? 'indirect' : 'direct');
    } catch (e) {
      return 'direct';
    }
  };

  const getInitialPriority = (): Priority => {
    try {
      const saved = sessionStorage.getItem('brr_priority');
      if (saved === 'high' || saved === 'low' || saved === 'medium') return saved;
      return 'medium';
    } catch (e) {
      return 'medium';
    }
  };

  const getInitialBrrLevel = (): BRRLevel | null => {
    try {
      const saved = sessionStorage.getItem('brr_brr_level');
      if (saved === 'level1' || saved === 'level2' || saved === 'na') return saved;
      return null;
    } catch (e) {
      return null;
    }
  };

  // Page routing: the URL is the source of truth for the current view.
  // Navigation calls navigate(VIEW_PATHS[...]); currentPage is derived from the
  // pathname. Rendering still uses the #823 keep-mounted display-toggle below.
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentPage = pathToView(pathname);
  const [visitedPages, setVisitedPages] = useState<Set<ViewType>>(new Set([getInitialView()]));
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(getInitialBatchId());
  const [selectedInspectionLotId, setSelectedInspectionLotId] = useState<string | null>(getInitialInspectionLotId());
  const [selectedLotIds, setSelectedLotIds] = useState<string[]>([]);
  const [selectedProductName, setSelectedProductName] = useState<string | null>(getInitialProductName());
  const [sessionId, setSessionId] = useState<string | null>(getInitialSessionId());
  const [selectedBatchType, setSelectedBatchType] = useState<'direct' | 'indirect'>(getInitialBatchType());
  const [selectedPriority, setSelectedPriority] = useState<Priority>(getInitialPriority());
  const [priorityLockedBy, setPriorityLockedBy] = useState<string | null>(null);
  const [selectedBrrLevel, setSelectedBrrLevel] = useState<BRRLevel | null>(getInitialBrrLevel());
  const [selectedIndirectTransportLotId, setSelectedIndirectTransportLotId] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [documentCounts, setDocumentCounts] = useState<DocumentCounts>({});
  const [orchestratorBoxArchive, setOrchestratorBoxArchive] = useState<BoxArchiveInfo | null>(null);

  // State for fieldOverrides, compliance data, and form data (shared between FormPage and ConfirmationPage)
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, any>>({});
  const [complianceOverrides, setComplianceOverrides] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<any>(null);
  const [additionalEvidenceItems, setAdditionalEvidenceItems] = useState<AdditionalEvidenceItem[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // State for submission confirmation (QP info, product details)
  const [submittedQPName, setSubmittedQPName] = useState<string>('');
  const [submittedQPEmail, setSubmittedQPEmail] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  // Note: country and batch dates are now handled via ValidatedStateContext
  // which persists to the session store and flows through useValidatedFormData

  // State for loading page (shown between Start BRR and Document Validation)
  const [showLoadingPage, setShowLoadingPage] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  // Track when orchestrator just completed - signals DocumentValidationPage to fetch fresh from blob
  // Persisted to sessionStorage to survive component remounts during navigation
  const [orchestratorJustCompleted, setOrchestratorJustCompleted] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('brr_orchestrator_just_completed') === 'true';
    } catch {
      return false;
    }
  });
  const [loadingStatus, setLoadingStatus] = useState<string>('');
  const [orchestratorError, setOrchestratorError] = useState<string | null>(null);

  // Read-only mode (when viewing another user's BRR from search)
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Delay/Expected Release fields (must be preserved across pages for session sync)
  const [selectedExpectedReleaseDate, setSelectedExpectedReleaseDate] = useState<string | null>(null);
  const [selectedDelayReason, setSelectedDelayReason] = useState<string | null>(null);
  const [selectedDelayDetails, setSelectedDelayDetails] = useState<string | null>(null);
  const [selectedDelayActions, setSelectedDelayActions] = useState<string | null>(null);
  const [selectedDelayCmoName, setSelectedDelayCmoName] = useState<string | null>(null);

  // Processing time tracking
  const [inProgressAt, setInProgressAt] = useState<string | null>(null);

  // Navigation blocking state (prevents navigation during load/save operations)
  const [isNavigationBlocked, setNavigationBlocked] = useState(false);
  const [navigationBlockMessage, setNavigationBlockMessage] = useState('');
  const [navigationSaveError, setNavigationSaveError] = useState<string | null>(null);

  // Progress tracking for load/save operations
  const {
    progressState,
    startOperation: startProgressOperation,
    advanceStep: advanceProgressStep,
    completeOperation: completeProgressOperation,
    cancelOperation: cancelProgressOperation,
  } = useProgressTracking();

  // On first mount, if the URL isn't one of our view paths (e.g. "/"), redirect
  // to the computed initial view. Uses replace so Back doesn't return to "/".
  useEffect(() => {
    if (!PATH_TO_VIEW[pathname]) {
      navigate(VIEW_PATHS[getInitialView()], { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Viewer role is restricted to dashboard only — redirect if they land elsewhere
  useEffect(() => {
    if (userRole === 'viewer' && currentPage !== 'dashboard') {
      navigate(VIEW_PATHS.dashboard, { replace: true });
    }
  }, [userRole, currentPage]);

  // Track visited pages for CSS display-toggle rendering
  useEffect(() => {
    setVisitedPages(prev => {
      if (prev.has(currentPage)) return prev;
      return new Set(prev).add(currentPage);
    });
  }, [currentPage]);

  // Convert progress state to overlay format
  const progressOverlayData: ProgressOverlayData | null = progressState
    ? {
        percentage: progressState.percentage,
        currentStepLabel: progressState.currentStepLabel,
        estimatedTimeRemaining: progressState.estimatedTimeRemaining,
        title: progressState.title,
      }
    : null;

  // Callbacks for pages to block/unblock navigation
  const blockNavigation = useCallback((message: string) => {
    setNavigationBlocked(true);
    setNavigationBlockMessage(message);
    setNavigationSaveError(null);
  }, []);

  const unblockNavigation = useCallback((error?: string | null) => {
    setNavigationBlocked(false);
    setNavigationBlockMessage('');
    if (error) {
      cancelProgressOperation();
      setNavigationSaveError(error);
    } else {
      completeProgressOperation();
    }
  }, [completeProgressOperation, cancelProgressOperation]);

  // Persist batch/session state to sessionStorage (for resuming work)
  // Note: brr_current_page is intentionally NOT persisted - app always starts at home
  useEffect(() => {
    try {
      if (selectedBatchId) {
        sessionStorage.setItem('brr_batch_id', selectedBatchId);
      } else {
        sessionStorage.removeItem('brr_batch_id');
      }
    } catch (e) {
      console.warn('Failed to save batch ID to sessionStorage:', e);
    }
  }, [selectedBatchId]);

  useEffect(() => {
    try {
      if (sessionId) {
        sessionStorage.setItem('brr_session_id', sessionId);
      } else {
        sessionStorage.removeItem('brr_session_id');
      }
    } catch (e) {
      console.warn('Failed to save session ID to sessionStorage:', e);
    }
  }, [sessionId]);

  useEffect(() => {
    try {
      if (selectedInspectionLotId) {
        sessionStorage.setItem('brr_inspection_lot_id', selectedInspectionLotId);
      } else {
        sessionStorage.removeItem('brr_inspection_lot_id');
      }
    } catch (e) {
      console.warn('Failed to save inspection lot ID to sessionStorage:', e);
    }
  }, [selectedInspectionLotId]);

  useEffect(() => {
    try {
      if (selectedProductName) {
        sessionStorage.setItem('brr_product_name', selectedProductName);
      } else {
        sessionStorage.removeItem('brr_product_name');
      }
    } catch (e) {
      console.warn('Failed to save product name to sessionStorage:', e);
    }
  }, [selectedProductName]);

  useEffect(() => {
    try {
      sessionStorage.setItem('brr_batch_type', selectedBatchType);
    } catch (e) {
      console.warn('Failed to save batch type to sessionStorage:', e);
    }
  }, [selectedBatchType]);

  useEffect(() => {
    try {
      sessionStorage.setItem('brr_priority', selectedPriority);
    } catch (e) {
      console.warn('Failed to save priority to sessionStorage:', e);
    }
  }, [selectedPriority]);

  useEffect(() => {
    try {
      if (selectedBrrLevel) {
        sessionStorage.setItem('brr_brr_level', selectedBrrLevel);
      } else {
        sessionStorage.removeItem('brr_brr_level');
      }
    } catch (e) {
      console.warn('Failed to save BRR level to sessionStorage:', e);
    }
  }, [selectedBrrLevel]);

  // Extract product name from form data when it changes
  // Note: country is NOT set here — it is only set by handleNextFromDocValidation
  // so that extractedCountry only overrides targetCountry when the user has actually
  // come through the Document Validation page.
  useEffect(() => {
    if (formData) {
      const prodName = formData.productMarketData?.productName?.value || '';
      setProductName(prodName);
    }
  }, [formData]);

  // Initialize API clients (memoized to prevent unnecessary re-creation)
  const apiClient = useMemo(() => createBRRApi({ baseUrl: apiBaseUrl }), [apiBaseUrl]);


  // Session/audit client uses the FastAPI session/audit backend.
  const sessionClient = useMemo(() => createSessionApi({ baseUrl: apiBaseUrl }), [apiBaseUrl]);

  // On mount: kill any stale orchestrator left by a previous page refresh
  useEffect(() => {
    const staleInstanceId = sessionStorage.getItem('brr_orchestrator_instance_id');
    const blobPrefix = sessionStorage.getItem('brr_blob_prefix');
    if (!staleInstanceId) return;

    const killAndClean = async () => {
      try {
        await apiClient.terminateOrchestrator(staleInstanceId);
      } catch (e) {
        console.warn('Failed to terminate stale orchestrator:', e);
      }
      if (blobPrefix) {
        try {
          await apiClient.cleanupBlobs(blobPrefix);
        } catch (e) {
          console.warn('Failed to cleanup stale blobs:', e);
        }
      }
      sessionStorage.removeItem('brr_orchestrator_instance_id');
      sessionStorage.removeItem('brr_blob_prefix');
      setShowLoadingPage(false);
      setOrchestratorError(null);
      setLoadingProgress(0);
      navigate(VIEW_PATHS['start-brr']);
    };

    killAndClean();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort: terminate orchestrator on page unload (refresh / tab close).
  // The cached token is used because the unload handler cannot await a silent
  // renewal — the page is already going away.
  useEffect(() => {
    const handleBeforeUnload = () => {
      const instanceId = sessionStorage.getItem('brr_orchestrator_instance_id');
      if (!instanceId || !/^[a-f0-9:-]+$/i.test(instanceId)) return;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-API-TOKEN': getApiToken(),
      };
      const token = getCachedIdToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      fetch(`${apiBaseUrl}/api/brr/terminate/${instanceId}`, {
        method: 'POST',
        headers,
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [apiBaseUrl]);

  // Persist page transition to the session store (fire-and-forget, never blocks navigation)
  const persistTransitionRef = useRef({ sessionClient, sessionId, selectedBatchId, userEmail });
  persistTransitionRef.current = { sessionClient, sessionId, selectedBatchId, userEmail };

  const persistPageTransition = useCallback((page: string) => {
    const ctx = persistTransitionRef.current;
    if (!ctx.sessionId || !ctx.selectedBatchId) return;

    Promise.all([
      ctx.sessionClient.createAuditLog({
        batchId: ctx.selectedBatchId,
        sessionId: ctx.sessionId,
        action: 100000000, // AuditAction.Started
        details: JSON.stringify({ page })
      }),
      ctx.sessionClient.updateSession(ctx.sessionId, {
        currentPage: page,
        lastSavedAt: new Date(),
        status: SessionStatus.InProgress
      })
    ]).catch(error => {
      console.error('Failed to persist page transition:', error);
    });
  }, []);

  // Navigation handlers
  const navigateToDashboard = useCallback(() => {
    setVisitedPages(new Set(['dashboard']));
    navigate(VIEW_PATHS['dashboard']);
    setSelectedBatchId(null);
    setSelectedInspectionLotId(null);
    setSelectedProductName(null);
    setSessionId(null);
    setIsReadOnly(false);
    setSelectedBrrLevel(null);
    try {
      sessionStorage.removeItem('brr_batch_id');
      sessionStorage.removeItem('brr_session_id');
      sessionStorage.removeItem('brr_inspection_lot_id');
      sessionStorage.removeItem('brr_product_name');
      sessionStorage.removeItem('brr_brr_level');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
  }, []);

  const navigateToStartBRR = useCallback(() => {
    // Sidebar navigation - preserve state so saved progress loads
    navigate(VIEW_PATHS['start-brr']);
  }, []);

  // Start fresh BRR - clears all workflow state (used by HomePage "Start New BRR" button)
  const handleStartNewBRR = useCallback(() => {
    setSelectedBatchId(null);
    setSelectedInspectionLotId(null);
    setSelectedProductName(null);
    setSessionId(null);
    setSelectedBatchType('direct');
    setSelectedPriority('medium');
    setSelectedBrrLevel(null);
    setSelectedIndirectTransportLotId(null);
    setSelectedDocuments([]);
    setDocumentCounts({});
    setFieldOverrides({});
    setComplianceOverrides({});
    setFormData(null);
    setAdditionalEvidenceItems([]);
    setIsReadOnly(false);
    try {
      sessionStorage.removeItem('brr_batch_id');
      sessionStorage.removeItem('brr_session_id');
      sessionStorage.removeItem('brr_inspection_lot_id');
      sessionStorage.removeItem('brr_product_name');
      sessionStorage.removeItem('brr_brr_level');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
    setVisitedPages(new Set(['start-brr']));
    navigate(VIEW_PATHS['start-brr']);
  }, []);

  const navigateToForm = useCallback((batchId: string, newSessionId?: string) => {
    setSelectedBatchId(batchId);
    if (newSessionId) {
      setSessionId(newSessionId);
    }
    navigate(VIEW_PATHS['form']);
    persistPageTransition('form');
  }, [persistPageTransition]);

  const navigateToConfirmation = useCallback(() => {
    navigate(VIEW_PATHS['qp-review']);
    persistPageTransition('qp-review');
  }, [persistPageTransition]);

  const navigateToFinalConfirmation = useCallback((qpName: string, qpEmail: string) => {
    // QP submission completed - return to home page
    navigate(VIEW_PATHS['home']);
  }, []);

  const navigateToHome = useCallback(() => {
    // Sidebar navigation to Home - preserve workflow state so user can navigate back
    navigate(VIEW_PATHS['home']);
  }, []);

  // Delete BRR - removes all data from Neo4j, the session store, and blob storage
  const handleDeleteBRR = useCallback(async () => {
    if (!selectedBatchId || !sessionId) {
      console.warn('[BRRApp] Cannot delete BRR: missing batchId or sessionId');
      return;
    }

    // Track which deletion steps failed for reporting
    const failedSteps: string[] = [];

    try {
      // 1. Delete from Neo4j
      try {
        await apiClient.deleteBRR(selectedBatchId, sessionId);
      } catch (e) {
        console.warn('[BRRApp] Failed to delete from Neo4j:', e);
        failedSteps.push('Neo4j');
      }

      // 2. Delete uploaded documents from blob storage (brr-documents container)
      // Pass sessionId to only delete this session's documents
      try {
        await apiClient.clearUserDocuments(sessionId);
      } catch (e) {
        console.warn('[BRRApp] Failed to clear blob documents:', e);
        failedSteps.push('Blob documents');
      }

      // 3. Delete validation results from blob storage (brr-results container)
      // Use session-scoped path: {user_name}/{session_id}
      try {
        const userName = userEmail.replace('@', '_').replace(/[^a-zA-Z0-9._-]/g, '_');
        const blobPrefix = `${userName}/${sessionId}`;
        await apiClient.cleanupBlobs(blobPrefix);
      } catch (e) {
        console.warn('[BRRApp] Failed to clear validation results:', e);
        failedSteps.push('Validation results');
      }

      // 4. Delete session from the session store
      try {
        await sessionClient.deleteSession(sessionId);
      } catch (e) {
        console.warn('[BRRApp] Failed to delete session:', e);
        failedSteps.push('Session');
      }

      // 5. Clear all local state
      setSelectedBatchId(null);
      setSelectedInspectionLotId(null);
      setSelectedProductName(null);
      setSessionId(null);
      setSelectedBatchType('direct');
      setSelectedPriority('medium');
      setSelectedBrrLevel(null);
      setSelectedDocuments([]);
      setDocumentCounts({});
      setFieldOverrides({});
      setComplianceOverrides({});
      setFormData(null);
      setAdditionalEvidenceItems([]);
      setSubmittedQPName('');
      setSubmittedQPEmail('');
      setProductName('');
      setIsReadOnly(false);
      setOrchestratorBoxArchive(null);

      // 6. Clear sessionStorage
      clearBrrSessionStorage();

      // 7. Report partial failures if any
      if (failedSteps.length > 0) {
        console.warn(
          '[BRRApp] Partial deletion - some data may remain in: %s. ' +
          'Local state cleared. Consider retrying or contacting support if issues persist.',
          failedSteps.join(', ')
        );
      }

      // 8. Navigate to home page
      setVisitedPages(new Set(['home']));
      navigate(VIEW_PATHS['home']);
    } catch (error) {
      console.error('[BRRApp] Failed to delete BRR:', error);
      throw error;
    }
  }, [selectedBatchId, sessionId, apiClient, sessionClient, userEmail]);

  const navigateToSearch = useCallback(() => {
    // Sidebar navigation to Search - preserve workflow state so user can navigate back
    navigate(VIEW_PATHS['search-page']);
  }, []);

  const navigateToDocumentValidation = useCallback(() => {
    navigate(VIEW_PATHS['document-validation']);
    persistPageTransition('document-validation');
  }, [persistPageTransition]);

  const confirmLeaveFormPage = useCallback((navigate: () => void) => {
    if (currentPage === 'form' && isFormDirty) {
      if (!window.confirm('You have unsaved changes on the BRR Form. If you leave now, unsaved changes may be lost.\n\nDo you want to leave this page?')) {
        return;
      }
    }
    setIsFormDirty(false);
    navigate();
  }, [currentPage, isFormDirty]);

  // Handle batch selection from dashboard
  const handleSelectBatch = useCallback((batch: Batch) => {
    // TODO: Create session via useSession hook or here
    setIsReadOnly(false);
    setVisitedPages(new Set(['form']));
    navigateToForm(batch.id);
  }, [navigateToForm]);

  // Handle start new BRR from StartBRRPage
  const handleStartBRR = useCallback(async (options: StartBRROptions) => {
    const {
      batchId, lotId, selectedLotIds: lotIds, productName, batchType, indirectTransportLotId,
      priority, selectedDocuments, brrLevel, documentCounts: docCounts,
      existingDocumentCounts: existingDocCounts, productUid, documentTypeMap,
      destinationCountry, delayReason, delayDetails, delayActions, delayCmoName,
      expectedReleaseDate, alreadyExtractedBlobs,
    } = options;

    setSelectedBatchId(batchId);
    setSelectedInspectionLotId(lotId);
    setSelectedLotIds(lotIds ?? []);
    setSelectedProductName(productName);
    setSelectedBatchType(batchType ?? 'direct');
    setSelectedPriority(priority ?? 'medium');
    setPriorityLockedBy(null);
    setSelectedDocuments(selectedDocuments ?? []);
    setSelectedBrrLevel(brrLevel ?? null);
    setSelectedIndirectTransportLotId(indirectTransportLotId ?? null);
    setDocumentCounts(docCounts ?? {});
    setSelectedCountry(destinationCountry || null);
    setSelectedExpectedReleaseDate(expectedReleaseDate ?? null);
    setSelectedDelayReason(delayReason ?? null);
    setSelectedDelayDetails(delayDetails ?? null);
    setSelectedDelayActions(delayActions ?? null);
    setSelectedDelayCmoName(delayCmoName ?? null);
    const inProgressTimestamp = new Date().toISOString();
    setInProgressAt(inProgressTimestamp);

    // Compute lotKey: canonical representation of selected lot(s) for display
    // Same logic as backend compute_lot_key(): sorted comma-separated list
    const lotKey = lotIds && lotIds.length > 0
      ? [...lotIds].sort().join(',')
      : lotId ?? null;

    // Persist user product selection to graph if a UID was provided
    const parsedBatchId = parseInt(batchId, 10);
    if (productUid && !isNaN(parsedBatchId)) {
      try {
        await apiClient.confirmProductMatch(parsedBatchId, productUid, userEmail);
      } catch (error) {
        console.warn('[BRRApp] Failed to confirm product match (non-blocking):', error);
      }
    }

    // Create or reuse the session with InProgress status and initial page
    // Declared outside the if-block so it's available for the orchestrator ID save below
    let activeSessionId = sessionId;
    try {
      // Only create a new session if one doesn't exist yet
      if (!activeSessionId) {
        const session = await sessionClient.createSession({
          batchId,
          currentPage: 'document-validation',
          productName,
        });
        activeSessionId = session.id;
        setSessionId(activeSessionId);
      }

      // Set status to InProgress and store inspection lot + product name + selected documents in details
      await sessionClient.updateSession(activeSessionId, {
        status: SessionStatus.InProgress,
        currentPage: 'document-validation',
        details: JSON.stringify({
          inspectionLotId: lotId,
          lotKey: lotKey,
          productName: productName,
          productUid: productUid ?? null,
          batchType: batchType ?? 'direct',
          priority: priority ?? 'medium',
          selectedDocuments: selectedDocuments ?? [],
          brrLevel: brrLevel ?? 'na',
          documentCounts: docCounts ?? {},
          delayReason: delayReason ?? null,
          delayDetails: delayDetails ?? null,
          delayActions: delayActions ?? null,
          delayCmoName: delayCmoName ?? null,
          expectedReleaseDate: expectedReleaseDate ?? null,
          inProgressAt: inProgressTimestamp,
        })
      });
    } catch (error) {
      console.error('Failed to create/update session:', error);
    }

    // Show loading page
    setShowLoadingPage(true);
    setLoadingProgress(0);
    setLoadingStatus('Initializing BRR process...');
    setOrchestratorError(null);

    try {
      // Start orchestrator with session_id for isolation
      const sanitizedEmail = userEmail.replace('@', '_').replace(/[^a-zA-Z0-9._-]/g, '_');
      // existingDocCounts arrives pre-converted to backend keys (e.g., "COA", "PACKAGING_MATERIALS")
      // by StartBRRPage via FRONTEND_TO_BACKEND_DOC_TYPE. Pass through directly.
      const existingCounts: Record<string, number> = {};
      if (existingDocCounts) {
        for (const [docType, count] of Object.entries(existingDocCounts)) {
          if (count > 0) {
            existingCounts[docType] = count;
          }
        }
      }

      const response = await apiClient.startBRRProcess({
        product_name: productName,
        batch_id: batchId,
        inspection_lot: lotId || undefined,
        session_id: activeSessionId || undefined,
        document_types: documentTypeMap,
        existing_document_counts: Object.keys(existingCounts).length > 0 ? existingCounts : undefined,
        already_extracted_blobs: alreadyExtractedBlobs && alreadyExtractedBlobs.length > 0 ? alreadyExtractedBlobs : undefined
      });

      // Persist instance ID + blob prefix so a refresh can terminate the orchestrator
      // Path: {userName}/{sessionId} for session isolation
      const effectivePrefix = activeSessionId ? `${sanitizedEmail}/${activeSessionId}` : sanitizedEmail;
      try {
        sessionStorage.setItem('brr_orchestrator_instance_id', response.id);
        sessionStorage.setItem('brr_blob_prefix', effectivePrefix);
      } catch (e) {
        console.warn('Failed to save orchestrator ID to sessionStorage:', e);
      }
      if (activeSessionId) {
        sessionClient.updateSession(activeSessionId, {
          details: JSON.stringify({
            inspectionLotId: lotId,
            lotKey: lotKey,
            productName: productName,
            productUid: productUid ?? null,
            batchType: batchType ?? 'direct',
            priority: priority ?? 'medium',
            selectedDocuments: selectedDocuments ?? [],
            brrLevel: brrLevel ?? 'na',
            documentCounts: docCounts ?? {},
            delayReason: delayReason ?? null,
            delayDetails: delayDetails ?? null,
            delayActions: delayActions ?? null,
            delayCmoName: delayCmoName ?? null,
            expectedReleaseDate: expectedReleaseDate ?? null,
            inProgressAt: inProgressTimestamp,
            orchestratorInstanceId: response.id,
            blobPrefix: effectivePrefix,
          })
        }).catch(e => console.warn('Failed to save orchestrator ID to the session store:', e));
      }

      // Poll status
      await pollOrchestratorStatus(response.id);
    } catch (error) {
      console.error('Failed to start BRR process:', error);
      setOrchestratorError(error instanceof Error ? error.message : 'Failed to start processing');
      setLoadingStatus('Error: Failed to start document processing');
    }
  }, [sessionClient, userEmail, apiClient, navigateToDocumentValidation, sessionId]);

  // Poll orchestrator status
  const pollOrchestratorStatus = useCallback(async (instanceId: string) => {
    const startTime = Date.now();
    const maxDuration = 20 * 60 * 1000; // 20 minutes (large PDFs need multiple chunked API calls)
    let pollInterval = 2000; // Start with 2 seconds

    const poll = async () => {
      const elapsed = Date.now() - startTime;

      if (elapsed > maxDuration) {
        sessionStorage.removeItem('brr_orchestrator_instance_id');
        sessionStorage.removeItem('brr_blob_prefix');
        setOrchestratorError('Processing timeout');
        setLoadingStatus('Timeout: Processing is taking longer than expected');
        return;
      }

      try {
        const status = await apiClient.getOrchestratorStatus(instanceId);

        if (status.customStatus) {
          setLoadingProgress(prev => Math.max(prev, status.customStatus!.progress));
          setLoadingStatus(status.customStatus.message);
        } else if (status.runtimeStatus === 'Running' || status.runtimeStatus === 'Pending') {
          setLoadingProgress(prev => Math.max(prev, 5));
          setLoadingStatus('Initializing document processing...');
        }

        if (status.runtimeStatus === 'Completed') {
          sessionStorage.removeItem('brr_orchestrator_instance_id');
          sessionStorage.removeItem('brr_blob_prefix');
          setLoadingProgress(100);
          setLoadingStatus('Processing complete!');

          // Check for errors in output
          if (status.output?.status === 'error') {
            setOrchestratorError(status.output.message || 'Processing failed');
            setLoadingStatus(`Error: ${status.output.message || 'Processing failed'}`);
            return;
          }

          // Capture box_archive from orchestrator output for FormPage
          if (status.output?.box_archive) {
            setOrchestratorBoxArchive(status.output.box_archive);
          }

          // Navigate to validation page (even if some docs failed)
          // Signal that fresh blob results should be fetched (not cached tablesSnapshot)
          // Persist to sessionStorage to survive component remounts during navigation
          setOrchestratorJustCompleted(true);
          try {
            sessionStorage.setItem('brr_orchestrator_just_completed', 'true');
          } catch (e) {
            console.warn('[BRRApp] Failed to persist orchestrator completion flag:', e);
          }
          setTimeout(() => {
            setShowLoadingPage(false);
            navigateToDocumentValidation();
          }, 1000);
          return;
        }

        if (status.runtimeStatus === 'Failed' || status.runtimeStatus === 'Terminated') {
          sessionStorage.removeItem('brr_orchestrator_instance_id');
          sessionStorage.removeItem('brr_blob_prefix');
          setOrchestratorError('Processing failed');
          setLoadingStatus('Error: Document processing failed');
          return;
        }

        // Continue polling
        if (elapsed > 60000) {
          pollInterval = 5000; // Slow down after 1 minute
        }
        setTimeout(poll, pollInterval);
      } catch (error) {
        sessionStorage.removeItem('brr_orchestrator_instance_id');
        sessionStorage.removeItem('brr_blob_prefix');
        console.error('Failed to poll status:', error);
        setOrchestratorError(error instanceof Error ? error.message : 'Status check failed');
        setLoadingStatus('Error: Failed to check processing status');
      }
    };

    poll();
  }, [apiClient, navigateToDocumentValidation]);

  // Handle final submission
  const handleSubmit = useCallback(() => {
    // After submission, return to dashboard
    navigateToDashboard();
  }, [navigateToDashboard]);

  // Memoized callbacks for child components
  const handleCancelToForm = useCallback(() => {
    navigate(VIEW_PATHS['form']);
  }, []);

  const handleBackToStartBRR = useCallback(() => {
    navigate(VIEW_PATHS['start-brr']);
  }, []);

  const handlePriorityChange = useCallback((p: Priority) => {
    setSelectedPriority(p);
  }, []);

  const handleNextFromDocValidation = useCallback((
    _extractedCountry?: string,
    extractedProductName?: string,
    extractedInspectionLot?: string,
    _extractedManufactureDate?: string | null,
    _extractedExpiryDate?: string | null
  ) => {
    // Note: Country and batch dates now flow through ValidatedStateContext
    // which persists to the session store and is loaded via useValidatedFormData in FormPage.
    // We no longer need to pass these as props.

    // Update product name for display purposes
    if (extractedProductName) {
      setSelectedProductName(extractedProductName);
    }
    // Only update inspection lot ID if one wasn't already entered on the start page
    // This prevents CMO Batch IDs from documents (e.g., "B39") from overwriting
    // the SAP Inspection Lot ID entered by the user (e.g., "10000015812")
    if (extractedInspectionLot && !selectedInspectionLotId) {
      setSelectedInspectionLotId(extractedInspectionLot);
    }

    navigateToForm(selectedBatchId!, sessionId || undefined);
  }, [navigateToForm, selectedBatchId, selectedInspectionLotId, selectedProductName, sessionId]);

  // Reset orchestrator flag after DocumentValidationPage has fetched fresh blob data
  const handleBlobRefreshComplete = useCallback(() => {
    setOrchestratorJustCompleted(false);
    try {
      sessionStorage.removeItem('brr_orchestrator_just_completed');
    } catch (e) {
      console.warn('[BRRApp] Failed to clear orchestrator completion flag:', e);
    }
  }, []);

  // Handle resuming a saved session from Recent Activity
  // Architecture: the session store holds the session pointer (id, currentPage), Neo4j is source of truth for all review data
  const handleResumeSession = useCallback(async (resumeSessionId: string) => {
    if (!resumeSessionId) {
      console.error('[BRRApp] Cannot resume session: sessionId is empty or undefined');
      return;
    }

    try {
      // Step 1: Get session pointer from the session store (batchId, currentPage)
      const session = await sessionClient.getSession(resumeSessionId);
      if (!session) {
        console.error('[BRRApp] Session not found:', resumeSessionId);
        return;
      }

      const cols = sessionClient.columns;
      const batchId = session[cols.batchid];
      const currentPage = session[cols.currentpage] as ViewType;

      // Step 2: Fetch full review from Neo4j (single source of truth)
      const savedReview = await apiClient.getSavedReview(batchId, resumeSessionId);

      // Step 3: Derive all state from Neo4j data
      let inspectionLotId: string | null = null;
      let lotIds: string[] = [];
      let productName: string | null = null;
      let batchType: 'direct' | 'indirect' = 'direct';
      let priority: Priority = 'medium';
      let documents: string[] = [];
      let brrLevelValue: BRRLevel | null = null;
      let docCounts: DocumentCounts = {};

      // Multi-instance document types that can have multiple files
      const MULTI_INSTANCE_DOC_TYPES = ['packaging', 'bulkCoa', 'bulkCoc', 'delivery', 'bom', 'deviation', 'transport', 'other'];

      if (savedReview) {
        // Extract from startPage (source of truth for documents)
        if (savedReview.startPage) {
          inspectionLotId = savedReview.startPage.inspectionLotId ?? null;
          lotIds = savedReview.startPage.selectedLotIds ?? [];
          productName = savedReview.startPage.productName ?? null;
          brrLevelValue = savedReview.startPage.brrLevel ?? null;

          // Derive selectedDocuments from uploadedDocuments
          if (savedReview.startPage.uploadedDocuments && savedReview.startPage.uploadedDocuments.length > 0) {
            const docTypes = savedReview.startPage.uploadedDocuments
              .flatMap(doc => doc.documentType === 'combined'
                ? (doc.documentTypes || [])
                : (doc.documentType ? [doc.documentType] : [])
              )
              .filter((type): type is string => !!type);
            documents = [...new Set(docTypes)];

            // Derive documentCounts for multi-instance types
            docTypes.forEach(docType => {
              if (MULTI_INSTANCE_DOC_TYPES.includes(docType)) {
                docCounts[docType] = (docCounts[docType] || 0) + 1;
              }
            });
          }
        }

        // Get batchType and priority from review level
        batchType = savedReview.batchType ?? 'direct';
        priority = savedReview.priority ?? 'medium';
        if (savedReview.priorityLockedBy) {
          setPriorityLockedBy(savedReview.priorityLockedBy);
        }
      } else {
        // Fallback to session details if Neo4j has no data (rare case - session exists but no save yet)
        try {
          const details = session[cols.details];
          if (details) {
            const parsed = JSON.parse(details);
            inspectionLotId = parsed.inspectionLotId || null;
            productName = parsed.productName || null;
            batchType = parsed.batchType || 'direct';
            const rawPriority = parsed.priority === 'normal' ? 'medium' : parsed.priority;
            priority = (rawPriority === 'high' || rawPriority === 'medium' || rawPriority === 'low') ? rawPriority : 'medium';
            documents = parsed.selectedDocuments || [];
            brrLevelValue = parsed.brrLevel || null;
            if (parsed.documentCounts) {
              docCounts = parsed.documentCounts;
            } else if (parsed.packagingMaterialsCount) {
              docCounts = { packaging: parsed.packagingMaterialsCount };
            }
          }
        } catch (e) {
          console.warn('Failed to parse session details:', e);
        }
      }

      // Fallback to productName column if still not set
      if (!productName) {
        productName = session[cols.productname] || null;
      }

      // Restore inProgressAt from session details (lives in details JSON, not savedReview)
      try {
        const rawDetails = session[cols.details];
        if (rawDetails) {
          const parsedDetails = JSON.parse(rawDetails);
          if (parsedDetails?.inProgressAt) {
            setInProgressAt(parsedDetails.inProgressAt);
          }
        }
      } catch { /* ignore parse errors */ }

      // Step 4: Set all state
      setSelectedBatchId(batchId);
      setSelectedInspectionLotId(inspectionLotId);
      setSelectedLotIds(lotIds);
      setSelectedProductName(productName);
      setSessionId(resumeSessionId);
      setSelectedBatchType(batchType);
      setSelectedPriority(priority);
      setSelectedBrrLevel(brrLevelValue);
      setSelectedIndirectTransportLotId(savedReview?.startPage?.indirectTransportLotId || null);
      setSelectedDocuments(documents);
      setDocumentCounts(docCounts);
      setSelectedCountry(savedReview?.startPage?.destinationCountry || null);
      setSelectedExpectedReleaseDate(savedReview?.startPage?.expectedReleaseDate || null);
      setSelectedDelayReason(savedReview?.startPage?.delayReason || null);
      setSelectedDelayDetails(savedReview?.startPage?.delayDetails || null);
      setSelectedDelayActions(savedReview?.startPage?.delayActions || null);
      setSelectedDelayCmoName(savedReview?.startPage?.delayCmoName || null);

      // Pre-populate formData from saved review to prevent blank render on ConfirmationPage
      if (savedReview?.brrForm?.autoPopulatedData) {
        setFormData(savedReview.brrForm.autoPopulatedData);
      } else if (savedReview?.qpReview?.autoPopulatedData) {
        setFormData(savedReview.qpReview.autoPopulatedData);
      }

      // Also restore field/compliance overrides if available
      if (savedReview?.brrForm?.fieldOverrides) {
        setFieldOverrides(savedReview.brrForm.fieldOverrides);
      }
      if (savedReview?.brrForm?.complianceOverrides) {
        setComplianceOverrides(savedReview.brrForm.complianceOverrides);
      }

      // Step 5: Navigate to the last saved page
      const validPages: ViewType[] = ['home', 'dashboard', 'start-brr', 'form', 'qp-review', 'document-validation', 'search-page'];
      let targetPage: ViewType = currentPage && validPages.includes(currentPage as ViewType)
        ? (currentPage as ViewType)
        : 'start-brr';

      setVisitedPages(new Set([targetPage]));
      navigate(VIEW_PATHS[targetPage]);
    } catch (error) {
      console.error('Failed to resume session:', error);
    }
  }, [sessionClient, apiClient]);

  // Handle session selection from search page
  const handleSearchSelectSession = useCallback((_batchId: string, searchSessionId: string, readOnly: boolean) => {
    setIsReadOnly(readOnly);
    handleResumeSession(searchSessionId);
  }, [handleResumeSession]);

  // Render all visited pages simultaneously — active page gets display:block, others display:none
  const renderPages = () => (
    <>
      {/* LoadingPage overlay — renders above all pages when active */}
      {showLoadingPage && (
        <LoadingPage
          batchNumber={selectedBatchId || 'No Data'}
          productName={selectedProductName || 'No Data'}
          progress={loadingProgress}
          status={loadingStatus}
          error={orchestratorError}
          onRetry={orchestratorError ? () => {
            setShowLoadingPage(false);
            setOrchestratorError(null);
            navigate(VIEW_PATHS['start-brr']);
          } : undefined}
        />
      )}

      {/* Display-toggled pages — hidden when loading overlay is active */}
      <div style={{ display: showLoadingPage ? 'none' : 'block' }}>
        {visitedPages.has('dashboard') && (
          <div style={{ display: currentPage === 'dashboard' ? 'block' : 'none' }}>
            <DashboardPage
              isActive={currentPage === 'dashboard'}
              userEmail={userEmail}
              userRole={userRole}
              sessionClient={sessionClient}
              apiClient={apiClient}
              onSelectBatch={handleSelectBatch}
            />
          </div>
        )}

        {visitedPages.has('connection-status') && (
          <div style={{ display: currentPage === 'connection-status' ? 'block' : 'none' }}>
            <ConnectionStatusPage isActive={currentPage === 'connection-status'} apiClient={apiClient} />
          </div>
        )}

        {visitedPages.has('start-brr') && (
          <div style={{ display: currentPage === 'start-brr' ? 'block' : 'none' }}>
            <StartBRRPage
              isActive={currentPage === 'start-brr'}
              onStart={handleStartBRR}
              onCancel={navigateToDashboard}
              onSessionCreated={setSessionId}
              onBatchIdChange={setSelectedBatchId}
              onBatchTypeChange={setSelectedBatchType}
              onResumeSession={handleResumeSession}
              apiClient={apiClient}
              sessionClient={sessionClient}
              sessionId={sessionId || ''}
              userEmail={userEmail}
              batchId={selectedBatchId || undefined}
              blockNavigation={blockNavigation}
              unblockNavigation={unblockNavigation}
              isNavigationBlocked={isNavigationBlocked}
            />
          </div>
        )}

        {visitedPages.has('form') && (
          <div style={{ display: currentPage === 'form' ? 'block' : 'none' }}>
            <ErrorBoundary>
              <FormPage
                isActive={currentPage === 'form'}
                batchId={selectedBatchId || ''}
                inspectionLotId={selectedInspectionLotId}
                selectedLotIds={selectedLotIds}
                productName={selectedProductName}
                sessionId={sessionId}
                userEmail={userEmail}
                apiClient={apiClient}
                sessionClient={sessionClient}
                onNext={navigateToConfirmation}
                onCancel={navigateToDashboard}
                isReadOnly={isReadOnly}
                fieldOverrides={fieldOverrides}
                setFieldOverrides={setFieldOverrides}
                complianceOverrides={complianceOverrides}
                setComplianceOverrides={setComplianceOverrides}
                onDataChange={setFormData}
                useRealData={!!selectedInspectionLotId}
                additionalEvidenceItems={additionalEvidenceItems}
                setAdditionalEvidenceItems={setAdditionalEvidenceItems}
                batchType={selectedBatchType}
                priority={selectedPriority}
                userRole={userRole}
                priorityLockedBy={priorityLockedBy}
                onPriorityChange={handlePriorityChange}
                brrLevel={selectedBrrLevel ?? 'level1'}
                startPageCountry={selectedCountry}
                orchestratorBoxArchive={orchestratorBoxArchive}
                onDeleteBRR={handleDeleteBRR}
                blockNavigation={blockNavigation}
                unblockNavigation={unblockNavigation}
                isNavigationBlocked={isNavigationBlocked}
                startProgressOperation={startProgressOperation}
                advanceProgressStep={advanceProgressStep}
                onDirtyChange={setIsFormDirty}
              />
            </ErrorBoundary>
          </div>
        )}

        {visitedPages.has('qp-review') && (
          <div style={{ display: currentPage === 'qp-review' ? 'block' : 'none' }}>
            <ErrorBoundary>
              <ConfirmationPage
                isActive={currentPage === 'qp-review'}
                batchId={selectedBatchId || ''}
                inspectionLotId={selectedInspectionLotId || undefined}
                selectedLotIds={selectedLotIds}
                indirectTransportLotId={selectedIndirectTransportLotId}
                sessionId={sessionId}
                userEmail={userEmail}
                apiClient={apiClient}
                sessionClient={sessionClient}
                onSubmit={navigateToFinalConfirmation}
                onCancel={handleCancelToForm}
                fieldOverrides={fieldOverrides}
                setFieldOverrides={setFieldOverrides}
                complianceOverrides={complianceOverrides}
                setComplianceOverrides={setComplianceOverrides}
                data={formData}
                additionalEvidenceItems={additionalEvidenceItems}
                setAdditionalEvidenceItems={setAdditionalEvidenceItems}
                onDeleteBRR={handleDeleteBRR}
                blockNavigation={blockNavigation}
                unblockNavigation={unblockNavigation}
                isNavigationBlocked={isNavigationBlocked}
                startProgressOperation={startProgressOperation}
                advanceProgressStep={advanceProgressStep}
                brrLevel={selectedBrrLevel}
              />
            </ErrorBoundary>
          </div>
        )}

        {visitedPages.has('home') && (
          <div style={{ display: currentPage === 'home' ? 'block' : 'none' }}>
            <HomePage
              isActive={currentPage === 'home'}
              onNavigateToSearch={navigateToSearch}
              onNavigateToStartBRR={handleStartNewBRR}
            />
          </div>
        )}

        {visitedPages.has('search-page') && (
          <div style={{ display: currentPage === 'search-page' ? 'block' : 'none' }}>
            <SearchPage
              isActive={currentPage === 'search-page'}
              userEmail={userEmail}
              sessionClient={sessionClient}
              apiClient={apiClient}
              onSelectBatch={handleSearchSelectSession}
            />
          </div>
        )}

        {visitedPages.has('document-validation') && (
          <div style={{ display: currentPage === 'document-validation' ? 'block' : 'none' }}>
            <DocumentValidationPage
              isActive={currentPage === 'document-validation'}
              batchId={selectedBatchId || ''}
              sessionId={sessionId || ''}
              apiClient={apiClient}
              sessionClient={sessionClient}
              userEmail={userEmail}
              productName={selectedProductName || undefined}
              selectedDocuments={selectedDocuments}
              documentCounts={documentCounts}
              batchType={selectedBatchType}
              inspectionLotId={selectedInspectionLotId || undefined}
              selectedLotIds={selectedLotIds}
              brrLevel={selectedBrrLevel ?? undefined}
              priority={selectedPriority}
              expectedReleaseDate={selectedExpectedReleaseDate}
              delayReason={selectedDelayReason}
              delayDetails={selectedDelayDetails}
              delayActions={selectedDelayActions}
              delayCmoName={selectedDelayCmoName}
              inProgressAt={inProgressAt}
              onNext={handleNextFromDocValidation}
              onBack={handleBackToStartBRR}
              onDeleteBRR={handleDeleteBRR}
              blockNavigation={blockNavigation}
              unblockNavigation={unblockNavigation}
              isNavigationBlocked={isNavigationBlocked}
              startProgressOperation={startProgressOperation}
              advanceProgressStep={advanceProgressStep}
              forceRefreshFromBlob={orchestratorJustCompleted}
              onBlobRefreshComplete={handleBlobRefreshComplete}
            />
          </div>
        )}

        {/* Fallback for unknown pages */}
        {!KNOWN_PAGES.includes(currentPage) && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
              <p className="text-gray-600 mb-6">The requested page does not exist</p>
              <button
                onClick={navigateToDashboard}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="w-full h-full bg-gray-50 flex overflow-hidden" style={{ height: '100%', overflow: 'hidden', display: 'flex' }}>
      {/* Left Navigation Sidebar */}
      <aside className="w-56 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto" style={{ flexShrink: 0, overflowY: 'auto' }}>
        {/* Brand Logo */}
        <div className="px-4 py-4 border-b border-gray-200">
          <img src={CHEPLAPHARM_LOGO} alt="Cheplapharm" className="h-8 w-auto" />
        </div>
        <nav className="p-2 text-left">
          <div className="space-y-1">
            {isSupervisorOrAbove && (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); if (!isNavigationBlocked) navigateToHome(); }}
                className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                  isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                  currentPage === 'home' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><Home size={20} /></span>
                <span className="text-sm">Home</span>
              </a>
            )}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); if (!isNavigationBlocked) confirmLeaveFormPage(navigateToDashboard); }}
              className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                currentPage === 'dashboard' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><BarChart3 size={20} /></span>
              <span className="text-sm">Dashboard</span>
            </a>
            {isAdmin && (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); if (!isNavigationBlocked) confirmLeaveFormPage(() => navigate(VIEW_PATHS['connection-status'])); }}
                className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                  isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                  currentPage === 'connection-status' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><AlertCircle size={20} /></span>
                <span className="text-sm">Connection Status</span>
              </a>
            )}
          </div>

          {isSupervisorOrAbove && (
            <div className="mt-6">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">BRR Management</div>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); if (!isNavigationBlocked) confirmLeaveFormPage(navigateToSearch); }}
                className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                  isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                  currentPage === 'search-page' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><Search size={20} /></span>
                <span className="text-sm">BRR Search</span>
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); if (!isNavigationBlocked) confirmLeaveFormPage(navigateToStartBRR); }}
                className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                  isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                  currentPage === 'start-brr' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><Upload size={20} /></span>
                <span className="text-sm">Start Batch Record Review</span>
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); if (!isNavigationBlocked) confirmLeaveFormPage(navigateToDocumentValidation); }}
                className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                  isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                  currentPage === 'document-validation' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><CheckSquare size={20} /></span>
                <span className="text-sm">Document Validation</span>
              </a>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (isNavigationBlocked) return;
                  navigate(VIEW_PATHS['form']);
                }}
                className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                  isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                  currentPage === 'form' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><FileText size={20} /></span>
                <span className="text-sm">Batch Record Review Form</span>
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); if (!isNavigationBlocked) confirmLeaveFormPage(() => navigate(VIEW_PATHS['qp-review'])); }}
                className={`flex items-center justify-start gap-2 px-3 py-2 rounded text-left ${
                  isNavigationBlocked ? 'opacity-50 cursor-not-allowed' :
                  currentPage === 'qp-review' ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center"><Users size={20} /></span>
                <span className="text-sm">QP Submission</span>
              </a>
            </div>
          )}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-0 min-w-0 overflow-y-auto relative" style={{ flex: '1 1 0%', minHeight: 0, minWidth: 0, overflowY: 'auto' }}>
        {/* Version indicator - hidden on loading page */}
        {!showLoadingPage && (
          <div
            className={`absolute top-2 right-4 text-xs text-gray-400 z-10${BUILD_TOOLTIP ? ' cursor-help' : ''}`}
            title={BUILD_TOOLTIP}
          >
            {formatVersionAsReleaseDate(APP_VERSION)}
          </div>
        )}
        <ValidatedStateProvider
          sessionId={sessionId}
          batchId={selectedBatchId || ''}
          sessionClient={sessionClient}
        >
          <div className="max-w-7xl mx-auto p-6">
            {renderPages()}
          </div>
        </ValidatedStateProvider>
      </main>
      <Footer message={STAGE_NAME}/>

      {/* Progress/Navigation Block Overlay */}
      <ProgressOverlay
        isBlocked={isNavigationBlocked}
        message={navigationBlockMessage}
        progress={progressOverlayData}
      />

      {/* Save Error Toast */}
      {navigationSaveError && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md animate-in slide-in-from-bottom-2">
          <div className="bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Save Failed</p>
              <p className="text-sm text-red-700 mt-1">{navigationSaveError}</p>
            </div>
            <button
              onClick={() => setNavigationSaveError(null)}
              className="text-red-400 hover:text-red-600 p-1"
            >
              <span className="sr-only">Dismiss</span>
              <span className="text-lg leading-none">&times;</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BRRApp;
